import { createMutation, createQuery } from 'react-query-kit';
import { camelCase, omit } from 'lodash';

import { FetchEndpoint } from '../create-api/build-endpoint.model';
import { EndpointsShape } from '../create-api/create-api.model';

import {
    type ApiHookOptions,
    type ApiHooks,
    EXTRA_OPTIONS,
    EXTRA_OPTIONS_CONFIG,
    type InferResponseRequest,
    METHOD_PREFIXES,
    METHOD_TYPES,
} from './create-api-hooks.model';
import { querySelectors } from './create-api-hooks.utils';

const attachExtraOptions = <T extends object>(
    hook: T,
    hookOptions: Record<string, any> | undefined,
    config: (typeof EXTRA_OPTIONS_CONFIG)[keyof typeof EXTRA_OPTIONS_CONFIG],
): T => {
    const optionsToAssign: Record<string, unknown> = {};
    for (const { key, fallback } of config) {
        optionsToAssign[key] = hookOptions?.[key] ?? fallback;
    }
    return Object.assign(hook, optionsToAssign);
};

const createApiHooks = <T extends EndpointsShape, O extends ApiHookOptions<T> = ApiHookOptions<T>>(
    api: T,
    options?: O,
) => {
    const hooks = { select: querySelectors } as ApiHooks<T, O>;

    for (const methodType of METHOD_TYPES) {
        const endpoints = api[methodType] as Record<string, FetchEndpoint<any, any>> | undefined;
        if (!endpoints) continue;

        for (const endpointName of Object.keys(endpoints) as (keyof typeof endpoints)[]) {
            const method = endpoints[endpointName];
            if (!method) continue;

            const hookKey = camelCase(`${METHOD_PREFIXES[methodType]}_${endpointName}`) as keyof ApiHookOptions<T>;
            const hookOptions = options?.[hookKey] as Record<string, any> | undefined;

            type ResponseRequest = InferResponseRequest<typeof method>;

            let hook: any;
            if (methodType === 'get') {
                hook = createQuery<ResponseRequest['Res'], ResponseRequest['Req'], Error>({
                    queryKey: [method.cachePrefix],
                    fetcher: method,
                    ...omit(hookOptions, EXTRA_OPTIONS),
                });
                attachExtraOptions(hook, hookOptions, EXTRA_OPTIONS_CONFIG.query);
            } else {
                hook = createMutation<ResponseRequest['Res'], ResponseRequest['Req'], Error>({
                    mutationKey: [method.cachePrefix],
                    mutationFn: method,
                    ...omit(hookOptions, EXTRA_OPTIONS),
                });
            }

            attachExtraOptions(hook, hookOptions, EXTRA_OPTIONS_CONFIG.global);
            hooks[hookKey] = hook;
        }
    }

    return hooks;
};

export { createApiHooks };
